import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useToast } from '../../modules/layout/components/ToastContext';
import { getAll, post } from "../_request";
import { IAxiosGetResponse, IClaimDifferenceDto, IClaimDto, IFilterClaimModel } from "../../interfaces/IUtilities";
import { useNavigate } from "react-router-dom";
import Loader from "../../modules/layout/components/Loader";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import { Field, Form, Formik } from "formik";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import SortDirection from "../../modules/layout/components/SortDirection";
import CapitalizeFirstLetter from "../../helpers/Utils";
import ConfirmModal from "../../modules/layout/components/ConfirmModal";
import Pagination from "../../modules/layout/components/Pagination";


const claimFields: (keyof IClaimDto)[] = [
    "id", "type", "value", "scope"
];

const ClaimList = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [claims, setClaims] = useState([] as IClaimDto[]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("name");
    const [asc, setAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    const [diffData, setDiffData] = useState([] as IClaimDifferenceDto[]);
    const [enableSync, setEnableSync] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [titleSync, setTitleSync] = useState('');
    const [pageDiff, setPageDiff] = useState(1);
    const [pageSizeDiff, setPageSizeDiff] = useState<number>(5);
    const [totalDiff, setTotalDiff] = useState(0);
    const [totalPagesDiff, setTotalPagesDiff] = useState(0);

    const initialFormState = {
        id: '',
        type: '',
        value: '',
        scope: ''
    }
    let order = asc ? "asc" : "desc";
    const [activeFilters, setActiveFilters] = useState<IFilterClaimModel>(initialFormState);

    const fetchClaims = async (filters: IFilterClaimModel = activeFilters) => {
        try {
            const params = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize },
                { key: 'sortBy', value: sortField },
                { key: 'order', value: order },
            ];
            //if (filters.id) params.push({ key: 'id', value: filters.id });
            if (filters.id) {
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                
                if (guidRegex.test(String(filters.id))) {
                    params.push({ key: 'id', value: filters.id });
                } else {
                    showToast(t("IdInvalidFormat"), 'warning');
                    setLoading(false);
                    return; 
                }
            };
            if (filters.type) params.push({ key: 'type', value: filters.type });
            if (filters.value) params.push({ key: 'value', value: filters.value });
            if (filters.scope) params.push({ key: 'scope', value: filters.scope });

            let result = await getAll('/api/claims/getall/',
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data as any;
            dataResults = dataResults["items"] as IClaimDto[];
            setTotal(result?.data.totalCount);
            setTotalPages(Math.ceil(result?.data.totalCount / result?.data.pageSize));

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setClaims(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus) }));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', { error: String(error) }));
            showToast(t('ConnectionError', { error: String(error) }), 'error');
            navigate('/');
        }
    }

    const fetchCompareClaims = async () => {
        try {
            let params = ``;
            let results = await getAll('/api/claims/compareclaims/', params);
            let respStatus = results?.status || results?.response.status;

            if (respStatus === 200) {
                let data = results.data as IClaimDifferenceDto[];
                setDiffData(data);
                setTotalDiff(data.length)
                setTotalPagesDiff(Math.ceil(data.length / pageSizeDiff));

                let sync = (data?.length > 0);
                setEnableSync(sync);
                setLoading(false);
            }
            else if (respStatus === 403) {
                navigate("/error/403");
            }
            else if (respStatus === 401) {
                navigate("/auth/login");
            }
            else if (respStatus === 500) {
                navigate("/error/500");
            }
            else {
                showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus) }));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', { error: String(error) }));
            showToast(t('ConnectionError', { error: String(error) }), 'error');
            navigate('/');
        }
    }

    const paginatedData = diffData.slice(
        (pageDiff - 1) * pageSizeDiff,
        pageDiff * pageSizeDiff
    );

    useEffect(() => {
        const fetchAll = async () => {
            await Promise.all([fetchClaims(), fetchCompareClaims()]);
        };
        fetchAll();
    }, [navigate, showToast, page, pageDiff, sortField, asc, pageSize, pageSizeDiff]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };

    const handleFilterSearch = async (filters: IFilterClaimModel) => {
        setPage(1);
        setActiveFilters(filters);
        await fetchClaims(filters);
    };

    const handleSyncClaim = () => {
        setShowModal(true);
        setTitleSync(t('SyncInDatabases'));
    };

    const handleConfirmSync = async () => {
        try {
            setLoading(true);
            let params = ``;
            let result = await post('/api/claims/syncclaims', params) as IAxiosGetResponse;
            let respStatus = result?.response.status;

            if (respStatus === 200) {
                let isSync = result.data as boolean;
                setLoading(false);
                await fetchClaims();
                await fetchCompareClaims();

                if (isSync) {
                    showToast(t('SyncInAWAndSAAddSuccess'), 'success');
                }
                else {
                    showToast(t('SyncInAWAndSAAddFail'), 'error');
                }
            }
            else if (respStatus === 403) {
                navigate("/error/403");
            }
            else if (respStatus === 401) {
                navigate("/auth/login");
            }
            else if (respStatus === 500) {
                navigate("/error/500");
            }
            else {
                showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus) }));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', { error: String(error) }));
            showToast(t('ConnectionError', { error: String(error) }), 'error');
            navigate('/');
        }

        setShowModal(false);
    };

    const handleCancelSync = () => {
        setShowModal(false);
    };


    return (
        <div className="gg-claimList container mt-4">
            <AppBreadcrumb
                items={[
                    { title: t('Home'), href: "/" },
                    { title: t('ClaimList') } // Active page (no href)
                ]} />

            <h3 className="mb-3">{t('ClaimList')}</h3>

            {loading && (<Loader />)}

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <div className="row">
                                <div className="col-6">
                                    <label className="form-label w-100">Id
                                        <Field className="form-control" name="id" />
                                    </label>
                                </div>
                                <div className="col6">
                                    <label className="form-label w-100">{t('Type')}
                                        <Field className="form-control" name="type" />
                                    </label>
                                </div>
                                <div className="col-6">
                                    <label className="form-label w-100">{t('Value')}
                                        <Field className="form-control" name="value" />
                                    </label>
                                </div>
                                <div className="col-6">
                                    <label className="form-label w-100">{t('Scope')}
                                        <Field className="form-control" name="scope" />
                                    </label>
                                </div>
                                <div className="col-6 align-self-end">
                                    <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                        {t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                        onClick={() => {
                                            resetForm();
                                            setActiveFilters(initialFormState);
                                            handleFilterSearch(initialFormState);
                                        }}>
                                        {t('Reset')}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: "30%" }} onClick={() => handleSort("type")}>{t('Type')}
                                <SortDirection isActive={sortField === 'type'} order={order} />
                            </th>
                            <th style={{ width: "30%" }} onClick={() => handleSort("value")}>{t('Value')}
                                <SortDirection isActive={sortField === 'code'} order={order} /></th>
                            <th style={{ width: "40%" }} onClick={() => handleSort("scope")}>{t('Scope')}
                                <SortDirection isActive={sortField === 'scope'} order={order} />
                            </th>
                        </tr>
                    </thead>
                    <tbody className="table-group-divider">
                        {claims.map(s => (
                            <tr key={s.id}>
                                <td>{s.type}</td>
                                <td>{s.value}</td>
                                <td>{s.scope}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={page}
                onPageChange={(page) => { setPage(page) }}
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
                totalItems={total} />

            {(diffData) ?
                <div className="gg-claim-diff mt-4">
                    <h4>{t('ClaimDataDifferences')}</h4>

                    <div className="row">
                        <div className="col">
                            <div className="table-responsive">
                                <table className="table table-bordered text-start w-full">
                                    <thead>
                                        <tr>
                                            {claimFields.map((field) => (
                                                <th key={field} className="p-1 text-left">
                                                    <CapitalizeFirstLetter text={field} />
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedData.map(diff => (
                                            <tr key={`diff-${diff.id}`}>
                                                {claimFields.map((field) => {
                                                    const adminWebClaim = diff.adminWebClaim as IClaimDto;
                                                    const keycloakClaim = diff.keycloakClaim as IClaimDto;

                                                    const adminValue = adminWebClaim?.[field as keyof typeof adminWebClaim];
                                                    const keycloakValue = keycloakClaim?.[field as keyof typeof keycloakClaim];
                                                    const isDifferent = diff.diffColumnsList?.includes(field);

                                                    switch (diff.differenceType) {
                                                        case 0:
                                                            return (
                                                                <td
                                                                    key={`diff-${diff.id}-${field}`}
                                                                    className="p-1 border text-primary">
                                                                    {String(keycloakValue)}
                                                                </td>
                                                            );

                                                        case 1:
                                                            return (
                                                                <td
                                                                    key={`diff-${diff.id}-${field}`}
                                                                    className="p-1 border text-danger">
                                                                    {String(adminValue)}
                                                                </td>
                                                            );

                                                        case 2:
                                                            return (
                                                                <td
                                                                    key={`diff-${diff.id}-${field}`}
                                                                    className={`p-1 border ${isDifferent ? 'text-warning fw-bold' : 'text-success'}`}>
                                                                    {String(keycloakValue)}
                                                                </td>
                                                            );

                                                        default:
                                                            return (
                                                                <td
                                                                    key={`diff-${diff.id}-${field}`}
                                                                    className="p-1 border text-primary">
                                                                    {String(keycloakValue)}
                                                                </td>
                                                            );
                                                    }
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={pageDiff}
                                onPageChange={(page) => setPageDiff(page)}
                                pageSize={pageSizeDiff}
                                onPageSizeChange={(size) => {
                                    setPageSizeDiff(size);
                                    setPageDiff(1);
                                }}
                                totalItems={totalDiff} />
                        </div>
                    </div>

                    <ConfirmModal
                        show={showModal}
                        title={titleSync}
                        message={t('SyncWithDatabase')}
                        onConfirm={handleConfirmSync}
                        onCancel={handleCancelSync}
                        buttonClass="warning"
                        loading={loading}
                    />

                    <div className="row my-2">
                        <div className="col d-flex justify-content-end align-items-end">
                            <button type="button" disabled={!enableSync} className="btn btn-warning"
                                onClick={handleSyncClaim}>{t('SyncInDatabases')}</button>
                        </div>
                    </div>

                </div>
                : <p className="text-warning">{t('NoDataDifferencesFoundBetweenAWAndKeycloak')}</p>}

        </div>
    );
};

export default ClaimList;
